import React from 'react';

/**
 * Artwork shared by the two signup pages, lifted verbatim from the design files
 * so the marks stay pixel-identical to what was signed off.
 */

/** The PawsFree face. `detail` carries the eye highlights, which are 1.4 units
 *  across in a 120 viewBox and turn to mud at the sizes this is used small at.
 *  The mouth is NOT part of that: it is a 2-unit stroke that still reads at
 *  30px, and dropping it left the step header with a blank-faced dog. */
export const PawsMark: React.FC<{ size?: number; detail?: boolean; earsBehind?: boolean }> = ({
  size = 80,
  detail = true,
  earsBehind = true,
}) => {
  const ears = (
    <>
      <ellipse cx="26" cy="57" rx="13" ry="27" fill="#C68A3F" transform="rotate(18 26 57)" />
      <ellipse cx="94" cy="57" rx="13" ry="27" fill="#C68A3F" transform="rotate(-18 94 57)" />
    </>
  );
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true">
      <ellipse cx="60" cy="62" rx="38" ry="36" fill="#E8B26B" />
      <ellipse cx="60" cy="78" rx="22" ry="16" fill="#F5D49A" />
      {earsBehind && ears}
      <ellipse cx="46" cy="60" rx="4" ry="5" fill="#2A1810" />
      {detail && <circle cx="47.5" cy="58.5" r="1.4" fill="#fff" />}
      <ellipse cx="74" cy="60" rx="4" ry="5" fill="#2A1810" />
      {detail && <circle cx="75.5" cy="58.5" r="1.4" fill="#fff" />}
      <ellipse cx="60" cy="74" rx="5" ry="4" fill="#3A2418" />
      <path d="M60 78 Q60 84 54 86" stroke="#3A2418" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M60 78 Q60 84 66 86" stroke="#3A2418" strokeWidth="2" fill="none" strokeLinecap="round" />
      {!earsBehind && ears}
    </svg>
  );
};

export const GoogleG: React.FC = () => (
  <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

export const AppleLogo: React.FC = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M16.36 1.43c0 1.14-.42 2.2-1.25 3.04-.9.92-2 1.45-3.03 1.36-.03-1.06.44-2.16 1.27-3 .87-.9 2.1-1.5 3.01-1.4zM20.6 17.1c-.55 1.28-.82 1.85-1.53 2.98-.99 1.58-2.38 3.55-4.1 3.56-1.53.02-1.93-.99-4.01-.98-2.08.01-2.52 1-4.05.98-1.72-.02-3.04-1.8-4.03-3.38C1.1 15.86.8 10.63 2.5 7.86c1.2-1.96 3.1-3.11 4.88-3.11 1.82 0 2.96 1 4.47 1 1.46 0 2.35-1 4.46-1 1.6 0 3.29.87 4.5 2.37-3.94 2.16-3.3 7.78 1.79 9.98z" />
  </svg>
);

export const MailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </svg>
);

export const Tick: React.FC<{ size?: number; width?: number }> = ({ size = 13, width = 3.4 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const WarnCircle: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4.5M12 16h.01" />
  </svg>
);

export const ChevronLeft: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

export const SearchIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </svg>
);

export const CloseIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

/**
 * The Chrome mark, in Google's colours rather than currentColor — it identifies
 * where the button goes (the Chrome Web Store), so a monochrome version would
 * read as decoration instead of a destination.
 *
 * Geometry: three 120° sectors of a circle at (24,24) r=20, cut on the radii at
 * 210° (upper left), 330° (upper right) and 90° (straight down), which puts red
 * across the top, yellow lower right and green lower left — the real mark's
 * arrangement, checked against Chrome's own app icon. The blue hub sits over the
 * middle at 47% of the diameter, on a white ring.
 */
export const ChromeMark: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 24 6.68 14A20 20 0 0 1 41.32 14Z" />
    <path fill="#FBBC04" d="M24 24 41.32 14A20 20 0 0 1 24 44Z" />
    <path fill="#34A853" d="M24 24 24 44A20 20 0 0 1 6.68 14Z" />
    <circle cx="24" cy="24" r="11.2" fill="#fff" />
    <circle cx="24" cy="24" r="9.4" fill="#4285F4" />
  </svg>
);
